import { Dialog, IconButton, Stack, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { Experiment, useCancelExperimentMutation } from "src/context/api/bidStratTesting/api";
import { BACKDROP_SX, COLORS, DangerButton, GreyButton, Icon } from "./components";
import { apiErrorMessage, formatDay } from "./utils";
import { tr } from "./i18n";

/** Confirmation of "Cancel Schedule" (lot 3): neither scraping nor the campaign change. */
export default function CancelScheduleDialog({ experiment, onClose }: { experiment: Experiment | null; onClose: () => void }) {
    const [cancelExperiment, { isLoading }] = useCancelExperimentMutation();

    const handleCancel = async () => {
        if (!experiment) return;
        try {
            await cancelExperiment(experiment.id).unwrap();
            enqueueSnackbar(tr("cancel.done"), { variant: "success" });
            onClose();
        } catch (error: any) {
            enqueueSnackbar(apiErrorMessage(error) ?? tr("cancel.error"), { variant: "error" });
        }
    };

    return (
        <Dialog
            slotProps={{ backdrop: { sx: BACKDROP_SX } }}
            open={!!experiment}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ sx: { width: 480, borderRadius: "16px", boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.3)", m: 2 } }}
        >
            <Stack
                direction="row"
                alignItems="center"
                sx={{ px: 3, py: 2.5, bgcolor: "rgba(226, 229, 233, 0.16)", borderBottom: `1px solid ${COLORS.border}` }}
            >
                <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, lineHeight: 1, color: COLORS.text }}>
                    {tr("cancel.title")}
                </Typography>
                <IconButton aria-label={tr("common.close")} onClick={onClose} size="small" sx={{ p: 0.5 }}>
                    <Icon name="x-close" size={16} />
                </IconButton>
            </Stack>
            <Stack gap={3} sx={{ p: 3 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4, color: COLORS.secondary }}>
                    {tr("cancel.body", { name: experiment?.name, date: formatDay(experiment?.start_date) })}
                </Typography>
                <Stack direction="row" justifyContent="flex-end" gap={1.5}>
                    <GreyButton onClick={onClose}>{tr("cancel.keep")}</GreyButton>
                    <DangerButton onClick={handleCancel} disabled={isLoading}>
                        {tr("table.cancel_schedule")}
                    </DangerButton>
                </Stack>
            </Stack>
        </Dialog>
    );
}
